import {
  DateInterval,
  DefaultActivityCategory,
  UserTimelinesModal,
} from '@wilson/interfaces';
import { sortIntervalByStartTime } from '@wilson/non-domain-specific/overlap-helpers';
import { determineInterval } from '@wilson/shift-timeline/services';
import { differenceInMinutes } from 'date-fns';

const TRANSFER_ACTIVITY_CATEGORY_NAMES = [
  DefaultActivityCategory.HomeTransfer,
  DefaultActivityCategory.InternalTransfer,
  DefaultActivityCategory.TaxiTransfer,
  DefaultActivityCategory.CarTransfer,
  DefaultActivityCategory.GuestRide,
];

export function calculateProductivityTimeDecimal(
  userTimeLine: UserTimelinesModal,
  { start, end }: Interval,
) {
  const sortedIntervals = createSortedIntervals(userTimeLine.shifts);

  if (sortedIntervals.length > 0) {
    const sortIntervalsWithTimeframe: DateInterval[] = [
      {
        startDatetime: new Date(start),
        endDatetime: new Date(start),
      },
      ...sortedIntervals,
      {
        startDatetime: new Date(end),
        endDatetime: new Date(end),
      },
    ];

    const sumOfRestMinutes = calculateSumOfRestMinutesBetweenShifts(
      sortIntervalsWithTimeframe,
    );
    const sumOfTransferMinutes = calculateTransferMinutes(userTimeLine.shifts);
    const timelineRangeInMinutes = differenceInMinutes(end, start);

    const productiveTime =
      timelineRangeInMinutes - sumOfRestMinutes - sumOfTransferMinutes;
    return productiveTime / timelineRangeInMinutes;
  } else {
    return 0;
  }
}

function calculateTransferMinutes(shifts: UserTimelinesModal['shifts']) {
  let sum = 0;
  shifts.forEach((shift) => {
    shift.activities.forEach((activity) => {
      if (
        TRANSFER_ACTIVITY_CATEGORY_NAMES.includes(
          activity.activityCategory?.name as DefaultActivityCategory,
        )
      ) {
        sum += differenceInMinutes(
          new Date(activity.endDatetime),
          new Date(activity.startDatetime),
        );
      }
    });
  });

  return sum;
}

function calculateSumOfRestMinutesBetweenShifts(
  sortedIntervals: DateInterval[],
) {
  let sum = 0;
  if (sortedIntervals.length > 1) {
    const MAX_REST_HOUR = 11;
    for (let i = 1; i < sortedIntervals.length; i++) {
      const currentInterval = sortedIntervals[i];
      const previousInterval = sortedIntervals[i - 1];
      const hourDifference = differenceInMinutes(
        currentInterval.startDatetime,
        previousInterval.endDatetime,
      );

      sum = sum + Math.max(hourDifference - MAX_REST_HOUR, 0);
    }
  }

  return sum;
}

function createSortedIntervals(shifts: UserTimelinesModal['shifts']) {
  const intervalList = shifts.map((shift) =>
    determineInterval(shift.activities),
  );
  intervalList.sort(sortIntervalByStartTime);
  return intervalList;
}
